import { useEffect, useRef, useState } from "react";

/**
 * Cursor-following radial spotlight. Renders an absolutely-positioned
 * div whose CSS variables --mx / --my track the mouse. Place inside a
 * `relative` parent — it covers the parent.
 */
export default function CursorSpotlight({ color = "rgba(227,255,52,0.07)" }) {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Disable on touch devices
    const isTouch = window.matchMedia("(hover: none)").matches;
    setEnabled(!isTouch);
  }, []);

  useEffect(() => {
    if (!enabled || !ref.current) return;
    const el = ref.current;
    const parent = el.parentElement;
    if (!parent) return;
    let raf = null;
    const onMove = (e) => {
      const r = parent.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        el.style.setProperty("--mx", `${x}px`);
        el.style.setProperty("--my", `${y}px`);
      });
    };
    parent.addEventListener("mousemove", onMove);
    return () => {
      parent.removeEventListener("mousemove", onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [enabled]);

  if (!enabled) return null;
  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none absolute inset-0 transition-opacity duration-300"
      style={{
        background: `radial-gradient(600px circle at var(--mx, 50%) var(--my, 50%), ${color}, transparent 55%)`,
      }}
    />
  );
}
